import { React, useState, useEffect, useCallback } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Area, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { StatCard, ChartTooltip, DateRangePicker } from '../dashboard';
import { AdminServiceKpi as AdminServiceKpiApi, AdminAnalytics as AdminAnalyticsApi } from '../../../api';

// Account = identity namespace (cross-service). This view shows only identity metrics
// (accounts registered/total). Per-service engagement (DAU/WAU/MAU/…) lives in the
// service dashboards under Account (e.g. Account > Loise), never here.
function AccountDashboard() {
    const [hours, setHours] = useState(720);
    const [kpi, setKpi] = useState({ loading: false });
    const [analytics, setAnalytics] = useState({ loading: false, rows: [] });
    const days = Math.max(1, Math.ceil(hours / 24));

    const refresh = useCallback(() => {
        AdminServiceKpiApi(setKpi, 'account');
        AdminAnalyticsApi(setAnalytics, days, 'account');
    }, [days]);

    // Keyed on hours, not days: sub-day presets all map to days=1 but should still refetch.
    useEffect(() => {
        refresh();
    }, [hours]); // eslint-disable-line react-hooks/exhaustive-deps

    const rows = analytics.rows.map((r) => ({ ...r, label: r.date?.slice(5) }));
    const loading = kpi.loading || analytics.loading;

    return (
        <Box sx={{ m: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: 2 }}>
                <IconButton size='small' onClick={refresh} disabled={loading}>
                    <RefreshIcon fontSize='small' />
                </IconButton>
                <DateRangePicker hours={hours} onChange={setHours} />
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
                <StatCard
                    label='total-accounts'
                    value={kpi.total_users}
                    loading={kpi.loading}
                    sparkline={rows}
                    sparklineKey='total_users'
                />
                <StatCard
                    label='new-accounts'
                    value={kpi.new_users}
                    loading={kpi.loading}
                    sub='Today'
                    accent
                    sparkline={rows}
                    sparklineKey='new_users'
                />
            </Box>

            <Typography variant='overline' color='text.secondary'>
                Registrations
            </Typography>
            <ResponsiveContainer width='100%' height={240}>
                <ComposedChart data={rows} margin={{ top: 4, right: 16, bottom: 0, left: 8 }}>
                    <defs>
                        <linearGradient id='acct-new-users' x1='0' y1='0' x2='0' y2='1'>
                            <stop offset='5%' stopColor='#1976d2' stopOpacity={0.15} />
                            <stop offset='95%' stopColor='#1976d2' stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id='acct-deleted-users' x1='0' y1='0' x2='0' y2='1'>
                            <stop offset='5%' stopColor='#d32f2f' stopOpacity={0.15} />
                            <stop offset='95%' stopColor='#d32f2f' stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke='#ebebeb' strokeOpacity={0.05} />
                    <XAxis dataKey='label' tick={{ fontSize: 10, fill: '#9e9e9e' }} axisLine={false} tickLine={false} />
                    <YAxis
                        yAxisId='left'
                        orientation='left'
                        width={32}
                        allowDecimals={false}
                        tick={{ fontSize: 10, fill: '#9e9e9e' }}
                        axisLine={false}
                        tickLine={false}
                        tickCount={5}
                    />
                    <YAxis
                        yAxisId='right'
                        orientation='right'
                        width={32}
                        allowDecimals={false}
                        tick={{ fontSize: 10, fill: '#9e9e9e' }}
                        axisLine={false}
                        tickLine={false}
                        tickCount={5}
                    />
                    <Tooltip content={<ChartTooltip />} cursor={false} />
                    <Legend wrapperStyle={{ fontSize: 12, color: '#9e9e9e' }} />
                    <Area
                        yAxisId='left'
                        type='monotone'
                        dataKey='new_users'
                        name='New'
                        stroke='#1976d2'
                        strokeWidth={1.5}
                        fill='url(#acct-new-users)'
                        dot={false}
                        activeDot={{ r: 3 }}
                    />
                    <Area
                        yAxisId='left'
                        type='monotone'
                        dataKey='deleted_users'
                        name='Deleted'
                        stroke='#d32f2f'
                        strokeWidth={1.5}
                        fill='url(#acct-deleted-users)'
                        dot={false}
                        activeDot={{ r: 3 }}
                    />
                    <Line
                        yAxisId='right'
                        type='monotone'
                        dataKey='total_users'
                        name='Total'
                        stroke='#388e3c'
                        strokeWidth={1.5}
                        dot={false}
                        activeDot={{ r: 3 }}
                    />
                </ComposedChart>
            </ResponsiveContainer>
        </Box>
    );
}

export { AccountDashboard };
